import type { FileFormatsUnion } from '@globalmed/atlas-ui';

/**
 * Builds a type guard that checks whether a value belongs to a known set of
 * literals. Use it instead of an `as` cast when a value arrives as a plain
 * string (from a UI component, an API payload or a route param) and has to be
 * narrowed to a union: the cast merely silences the compiler, whereas the guard
 * also rejects unexpected values at runtime.
 * @param values - The allowed values of the union.
 * @returns A type guard narrowing an unknown value to one of the given values.
 */
export const createUnionGuard =
  <T extends string | number>(values: readonly T[]) =>
  (value: unknown): value is T =>
    values.includes(value as T);

/**
 * Builds a type guard for a string enum, based on its runtime members.
 * @param enumObject - The enum object to check against.
 * @returns A type guard narrowing an unknown value to a member of the enum.
 */
export const createEnumGuard =
  <T extends Record<string, string>>(enumObject: T) =>
  (value: unknown): value is T[keyof T] =>
    Object.values(enumObject).includes(value as T[keyof T]);

/**
 * The file extensions Atlas UI's Attachment can render an icon for. Declared
 * here rather than imported from the library so that tests mocking
 * `@globalmed/atlas-ui` keep working.
 */
const FILE_FORMATS = [
  'avi',
  'css',
  'csv',
  'dmg',
  'doc',
  'docx',
  'eps',
  'exe',
  'gif',
  'html',
  'img',
  'java',
  'jpeg',
  'jpg',
  'js',
  'json',
  'mkv',
  'mp3',
  'mp4',
  'mpeg',
  'pdf',
  'png',
  'ppt',
  'pptx',
  'rar',
  'rss',
  'sql',
  'svg',
  'tiff',
  'txt',
  'wav',
  'webp',
  'xls',
  'xlsx',
  'xml',
  'zip',
] as const satisfies readonly FileFormatsUnion[];

/**
 * Checks whether a runtime file extension is one Attachment can display.
 * @param value - The extension to check.
 * @returns True if the extension is a supported file format.
 */
export const isFileFormat = (value: unknown): value is FileFormatsUnion =>
  FILE_FORMATS.includes(value as FileFormatsUnion);
